<script setup lang="ts">
import { computed, ref, shallowRef, onBeforeMount, onMounted } from 'vue';
import type { Component } from 'vue';
import type { BannerItem } from '~/utils/perfilOficialConfigs';
import { useOfficialProfilesList } from '~/composables/apostatudo/use-official-profiles-list';
import { fetchPerfilOficialBannersHandler } from '~/handlers/fetch-perfil-oficial-banners.handler';

const router = useRouter();
const { isDesktop } = useLayout();

// ── Banner ──────────────────────────────────────────────────────────────────
const desktopBanners = ref<BannerItem[]>([]);
const mobileBanners = ref<BannerItem[]>([]);

const Swiper = shallowRef<Component | null>(null);
const SwiperSlide = shallowRef<Component | null>(null);
const swiperLoaded = ref(false);

onBeforeMount(async () => {
  const [swiperVue, swiperElement] = await Promise.all([
    import('swiper/vue'),
    import('swiper/element/bundle'),
  ]);

  Swiper.value = swiperVue.Swiper;
  SwiperSlide.value = swiperVue.SwiperSlide;
  swiperElement.register();
  swiperLoaded.value = true;
});

onMounted(async () => {
  const [desktopResult, mobileResult] = await Promise.all([
    fetchPerfilOficialBannersHandler({ slug: 'perfil-oficial-desktop' }),
    fetchPerfilOficialBannersHandler({ slug: 'perfil-oficial-mobile' }),
  ]);

  desktopBanners.value = desktopResult.banners;
  mobileBanners.value = mobileResult.banners;
});

const shouldEnableDesktopLoop = computed(
  () => desktopBanners.value.length >= 3,
);

const shouldEnableMobileLoop = computed(() => mobileBanners.value.length >= 3);
const hasDesktopBanners = computed(() => desktopBanners.value.length > 0);
const hasMobileBanners = computed(() => mobileBanners.value.length > 0);

function goBack() {
  if (window.history.length > 1) {
    router.back();
  } else {
    navigateTo('/');
  }
}

// ── Filters ──────────────────────────────────────────────────────────────────
const searchQuery = ref('');

// Perfis da API do core, com fallback pro config hardcoded (ver o composable)
const { items: affiliatesList, isLoading: isLoadingProfiles } =
  useOfficialProfilesList();

const ALL_FILTER = 'Todos';

const selectedFilter = ref(ALL_FILTER);

/**
 * Chips derivados dos rótulos dos próprios perfis, em vez de uma lista fixa —
 * assim não existe chip que não casa com perfil nenhum, nem perfil com rótulo
 * que não tem chip.
 */
const availableFilters = computed(() => {
  const labels = new Set<string>();

  affiliatesList.value.forEach(({ config }) => {
    config.categories.forEach((category) => labels.add(category));
  });

  if (labels.size === 0) return [];

  return [
    ALL_FILTER,
    ...[...labels].sort((a, b) => a.localeCompare(b, 'pt-BR')),
  ];
});

// Uma barra com um chip só não filtra nada: com um único perfil publicado (ou
// enquanto o core não manda `tags` no índice) ela nem aparece, e volta sozinha
// quando houver rótulo suficiente pra escolha valer.
const isFilterBarVisible = computed(() => availableFilters.value.length > 2);

// O chip selecionado pode sumir quando a lista de perfis muda.
watch(availableFilters, (filters) => {
  if (!filters.includes(selectedFilter.value))
    selectedFilter.value = ALL_FILTER;
});

function selectFilter(filter: string) {
  selectedFilter.value = filter;
}

const filteredAffiliates = computed(() => {
  let result = affiliatesList.value;

  if (isFilterBarVisible.value && selectedFilter.value !== ALL_FILTER) {
    result = result.filter(({ config }) =>
      config.categories.includes(selectedFilter.value),
    );
  }

  if (searchQuery.value.trim()) {
    const query = searchQuery.value.toLowerCase().trim();

    result = result.filter(({ affiliateName, config }) => {
      const displayName = config.affiliateDetails?.name || affiliateName;

      if (displayName.toLowerCase().includes(query)) return true;
      if (config.categories.some((c) => c.toLowerCase().includes(query)))
        return true;
      if (config.affiliateDetails?.title?.toLowerCase().includes(query))
        return true;

      return false;
    });
  }

  return result;
});
</script>

<template>
  <div class="container mx-auto min-h-[80vh]">
    <!-- Banner desktop -->
    <div
      v-if="isDesktop && hasDesktopBanners && swiperLoaded"
      :class="$style.bannerWrapper"
    >
      <component
        :is="Swiper"
        :autoplay="{
          delay: 6000,
          pauseOnMouseEnter: true,
          disableOnInteraction: false,
        }"
        create-elements
        :loop="shouldEnableDesktopLoop"
        preload-images="false"
        :slides-per-group="1"
        :slides-per-view="1"
        :space-between="0"
        :speed="500"
      >
        <component
          :is="SwiperSlide"
          v-for="(banner, index) in desktopBanners"
          :key="index"
        >
          <NuxtLink
            class="block w-full"
            no-prefetch
            :to="banner.linkUrl || '/'"
          >
            <img
              :alt="banner.alt"
              :class="$style.bannerImage"
              :loading="index === 0 ? 'eager' : 'lazy'"
              :src="banner.image"
            />
          </NuxtLink>
        </component>
      </component>
    </div>

    <!-- Banner mobile -->
    <div
      v-if="!isDesktop && hasMobileBanners && swiperLoaded"
      :class="$style.mobileBannerWrapper"
    >
      <button
        :class="$style.backButton"
        @click="goBack"
      >
        <NuxtIcon
          :class="$style.backButtonIcon"
          :filled="false"
          name="arrows/chevron_left"
        />
        <span :class="$style.backButtonText">Voltar</span>
      </button>

      <component
        :is="Swiper"
        :autoplay="{
          delay: 6000,
          pauseOnMouseEnter: true,
          disableOnInteraction: false,
        }"
        create-elements
        :loop="shouldEnableMobileLoop"
        preload-images="false"
        :slides-per-group="1"
        :slides-per-view="1"
        :space-between="0"
        :speed="500"
      >
        <component
          :is="SwiperSlide"
          v-for="(banner, index) in mobileBanners"
          :key="index"
        >
          <NuxtLink
            class="block w-full"
            no-prefetch
            :to="banner.linkUrl || '/'"
          >
            <img
              :alt="banner.alt"
              :class="$style.mobileBannerImage"
              :loading="index === 0 ? 'eager' : 'lazy'"
              :src="banner.image"
            />
          </NuxtLink>
        </component>
      </component>

      <!-- Destaques Oficiais overlay -->
      <div :class="$style.destaquesOverlay">
        <svg
          :class="$style.destaquesIcon"
          fill="none"
          viewBox="0 0 62 61"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M31 0C35.4562 0 39.3312 2.46621 41.2809 6.08809C45.2648 4.88477 49.7695 5.83789 52.918 8.93555C56.0664 12.0332 57.0352 16.4771 55.8121 20.385C59.4934 22.3031 62 26.1156 62 30.5C62 34.8844 59.4934 38.6969 55.8121 40.615C57.0352 44.5348 56.0664 48.9668 52.918 52.0645C49.7695 55.1621 45.2527 56.1152 41.2809 54.9119C39.3312 58.5338 35.4562 61 31 61C26.5438 61 22.6688 58.5338 20.7191 54.9119C16.7352 56.1152 12.2305 55.1621 9.08203 52.0645C5.93359 48.9668 4.96484 44.5229 6.18789 40.615C2.50664 38.6969 0 34.8844 0 30.5C0 26.1156 2.50664 22.3031 6.18789 20.385C4.96484 16.4652 5.93359 12.0332 9.08203 8.93555C12.2305 5.83789 16.7473 4.88477 20.7191 6.08809C22.6688 2.46621 26.5438 0 31 0ZM44.6836 24.9004L46.7422 22.875L42.625 18.8361L40.5664 20.8615L27.125 34.0861L21.4336 28.4865L19.375 26.4611L15.2699 30.5L17.3285 32.5254L25.0785 40.1504L27.1371 42.1758L29.1957 40.1504L44.6836 24.9004Z"
            fill="url(#badge_grad)"
          />
          <defs>
            <linearGradient
              id="badge_grad"
              gradientUnits="userSpaceOnUse"
              x1="31"
              x2="31"
              y1="0"
              y2="61"
            >
              <stop stop-color="#00C9FF" />
              <stop
                offset="1"
                stop-color="#07112C"
              />
            </linearGradient>
          </defs>
        </svg>
        <div :class="$style.destaquesTexts">
          <p :class="$style.destaquesTitle">DESTAQUES OFICIAIS</p>
          <p :class="$style.destaquesSubtitle"
            >Parceiros e páginas da Apostatudo</p
          >
        </div>
      </div>
    </div>

    <!-- Search Bar -->
    <div class="w-full mb-4">
      <div class="relative">
        <input
          v-model="searchQuery"
          :class="$style.searchInput"
          placeholder="Buscar perfil"
          type="text"
        />
        <div class="absolute inset-y-0 right-0 flex items-center pr-4">
          <svg
            :class="$style.searchIcon"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
            />
          </svg>
        </div>
      </div>
    </div>

    <!-- Filter Swiper -->
    <div
      v-if="isFilterBarVisible"
      :class="$style.filterSwiper"
    >
      <button
        v-for="filter in availableFilters"
        :key="filter"
        :class="[
          $style.filterChip,
          { [$style.active]: selectedFilter === filter },
        ]"
        @click="selectFilter(filter)"
      >
        {{ filter }}
      </button>
    </div>

    <!-- Cards -->
    <div
      :class="[
        $style.cardsGrid,
        'gap-2 max-w-7xl mb-4 lg:flex lg:flex-wrap lg:justify-start',
      ]"
    >
      <PerfilOficialCard
        v-for="{ affiliateName, config } in filteredAffiliates"
        :key="affiliateName"
        :affiliate-name="affiliateName"
        :config="config"
        :fluid-width="!isDesktop"
      />
      <p
        v-if="!isLoadingProfiles && filteredAffiliates.length === 0"
        class="text-white/50 text-sm py-8"
      >
        Nenhum perfil encontrado.
      </p>
    </div>

    <h1 :class="$style.pageTitle">
      Perfis Oficiais: parceiros oficiais da Apostatudo
    </h1>
  </div>
</template>

<style module lang="postcss">
.cardsGrid {
  display: grid;
  /* 176px = metade de (393px - 32px padding - 8px gap): garante 2 colunas
     em 393px e colapsa para 1 abaixo disso (auto-fit recolhe tracks vazios) */
  grid-template-columns: repeat(auto-fit, minmax(176px, 1fr));
  justify-content: center;
  justify-items: center;
}

@media (min-width: 1024px) {
  .cardsGrid {
    display: flex;
    flex-wrap: wrap;
    justify-content: flex-start;
    justify-items: normal;
  }
}

/* Campo de busca de perfis — paleta navy do Figma sobre fundo branco */
.searchInput {
  @apply w-full px-4 py-3 bg-white rounded-md font-bold text-sm focus:outline-none;
  border: 1px solid #050c1f; /* Figma: borda navy */
  color: #0c1b44; /* Figma: texto navy */
  backdrop-filter: blur(10px); /* Figma: blur 10px */
}

.searchInput::placeholder {
  color: #0c1b44; /* Figma: placeholder navy */
}

.searchIcon {
  @apply w-5 h-5;
  color: #0c1b44; /* Figma: ícone navy */
}

.bannerWrapper {
  @apply w-full mb-6;
  border-radius: 20px;
  overflow: hidden;
}

.bannerImage {
  @apply w-full h-auto block object-cover;
  border-radius: 20px;
  aspect-ratio: 1240 / 300;
  user-select: none;
  -webkit-user-drag: none;
}

.mobileBannerWrapper {
  @apply relative mb-4;
  overflow: hidden;
  width: 100vw;
  left: 50%;
  transform: translateX(-50%);
}

.mobileBannerImage {
  @apply block object-cover w-full;
  height: auto;
  aspect-ratio: 375 / 220;
  user-select: none;
  -webkit-user-drag: none;
}

.destaquesOverlay {
  @apply absolute inset-x-0 bottom-0 flex flex-col items-center;
  z-index: 10;
  padding-bottom: 14px;
  background: linear-gradient(
    to top,
    rgba(0, 7, 23, 0.75) 0%,
    transparent 100%
  );
  pointer-events: none;
}

.destaquesIcon {
  width: 62px;
  height: 61px;
  margin-bottom: -9px;
  flex-shrink: 0;
}

.destaquesTexts {
  @apply flex flex-col items-center;
  gap: 2px;
}

.destaquesTitle {
  color: #fff;
  font-family: 'Kensington Test', sans-serif;
  font-size: 32.938px; /* Figma: 32.938px + scaleX(0.82) para comprimir o texto */
  font-weight: 700;
  line-height: 34px;
  text-transform: uppercase;
  letter-spacing: -0.5px;
  transform: scaleX(0.82);
  white-space: nowrap;
}

.destaquesSubtitle {
  color: #58c0fa;
  font-size: 12px;
  font-weight: 700;
  line-height: 16px;
  text-align: center;
  white-space: nowrap;
}

.backButton {
  @apply absolute top-3 left-3 z-10 inline-flex items-center justify-center;
  height: 32px;
  padding: 0 12px;
  gap: 8px;
  border-radius: 12px;
  border: 2px solid #272f3f;
  background: #272f3f;
  cursor: pointer;
}

.backButtonIcon {
  font-size: 20px;
  color: #fff;
  flex-shrink: 0;
}

.backButtonText {
  color: #fff;
  font-size: 12px;
  font-weight: 400;
  line-height: 16px;
  white-space: nowrap;
}

.pageTitle {
  font-size: 12px;
  font-weight: 600;
  line-height: 16px;
  color: rgba(212, 212, 216, 0.4);
  text-align: center;
  margin-top: 12px;
  margin-bottom: 24px;
}

.sectionTitle {
  border-radius: 6px;
  background: linear-gradient(
    270deg,
    rgba(147, 164, 213, 0) 0%,
    rgba(191, 205, 243, 0.15) 100%
  );
  padding: 6px 10px;
  margin-bottom: 12px;
}

.sectionTitleInner {
  @apply flex items-center;
  gap: 10px;
}

.sectionTitleIcon {
  font-size: 18px;
  width: 18px;
  height: 18px;
  flex-shrink: 0;

  @media (min-width: 1024px) {
    font-size: 31px;
    width: 31px;
    height: 30px;

    svg {
      width: 31px;
      height: 30px;
    }
  }
}

.filterSwiper {
  @apply flex items-center mb-6;
  gap: 6px;
  overflow-x: auto;
  flex-wrap: nowrap;
  scrollbar-width: none;
  -ms-overflow-style: none;

  &::-webkit-scrollbar {
    display: none;
  }
}

.filterChip {
  @apply flex-shrink-0 cursor-pointer flex items-center justify-center;
  height: 32px;
  padding: 0 12px;
  background: #272f3f;
  color: #fff;
  font-size: 12px;
  font-weight: 400;
  line-height: 16px;
  border-radius: 12px;
  border: none;
  white-space: nowrap;
  transition: opacity 0.15s;

  &:hover {
    opacity: 0.8;
  }
}

.active {
  padding: 0 14px;
  border-radius: 14px;
  border: 1px solid #1414dd;
  background: linear-gradient(
    90deg,
    rgba(20, 20, 221, 0.7) 0%,
    rgba(20, 20, 221, 0.2) 100%
  );
}

.sectionTitleText {
  color: #fff;
  font-size: 15.33px; /* Figma: 15.33px */
  font-weight: 700;
  line-height: 16.352px; /* Figma: 106.667% */
  white-space: nowrap;
}
</style>
